const DB_NAME = 'EthicsGlobalDB';
const DB_VERSION = 2;
const STORES = {
  AUDIO_RECORDINGS: 'audioRecordings',
  PENDING_SUBMISSIONS: 'pendingSubmissions',
};

let dbInstance = null;

const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Error opening IndexedDB'));
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORES.AUDIO_RECORDINGS)) {
        const store = db.createObjectStore(STORES.AUDIO_RECORDINGS, { keyPath: 'id' });
        store.createIndex('interviewId', 'interviewId', { unique: false });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // New store for pending submissions (offline queue)
      if (!db.objectStoreNames.contains(STORES.PENDING_SUBMISSIONS)) {
        const submissionsStore = db.createObjectStore(STORES.PENDING_SUBMISSIONS, { keyPath: 'id' });
        submissionsStore.createIndex('type', 'type', { unique: false });
        submissionsStore.createIndex('status', 'status', { unique: false });
        submissionsStore.createIndex('createdAt', 'createdAt', { unique: false });
        submissionsStore.createIndex('interviewId', 'interviewId', { unique: false });
      }
    };
  });
};

export const saveAudioChunk = async (data) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readwrite');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);

    const record = {
      id: generateId(),
      interviewId: data.interviewId,
      alias: data.alias,
      chunkIndex: data.chunkIndex,
      blob: data.blob,
      duration: data.duration,
      createdAt: new Date().toISOString(),
      status: 'pending',
      retryCount: 0,
    };

    const request = store.add(record);

    request.onsuccess = () => resolve(record);
    request.onerror = () => reject(new Error('Error saving audio chunk'));
  });
};

export const getAudioChunks = async (interviewId) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readonly');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);
    const index = store.index('interviewId');
    const request = index.getAll(interviewId);

    request.onsuccess = () => {
      const chunks = request.result.sort((a, b) => a.chunkIndex - b.chunkIndex);
      resolve(chunks);
    };
    request.onerror = () => reject(new Error('Error getting audio chunks'));
  });
};

export const markAsUploaded = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readwrite');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.status = 'uploaded';
        const updateRequest = store.put(record);
        updateRequest.onsuccess = () => resolve(record);
        updateRequest.onerror = () => reject(new Error('Error updating record'));
      } else {
        reject(new Error('Record not found'));
      }
    };
    getRequest.onerror = () => reject(new Error('Error getting record'));
  });
};

export const getPendingUploads = async () => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readonly');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('Error getting pending uploads'));
  });
};

export const deleteAudioChunk = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readwrite');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(new Error('Error deleting audio chunk'));
  });
};

export const clearInterviewAudio = async (interviewId) => {
  const chunks = await getAudioChunks(interviewId);

  for (const chunk of chunks) {
    await deleteAudioChunk(chunk.id);
  }

  return true;
};

export const incrementRetryCount = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.AUDIO_RECORDINGS], 'readwrite');
    const store = transaction.objectStore(STORES.AUDIO_RECORDINGS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.retryCount += 1;
        if (record.retryCount >= 5) {
          record.status = 'error';
        }
        const updateRequest = store.put(record);
        updateRequest.onsuccess = () => resolve(record);
        updateRequest.onerror = () => reject(new Error('Error updating retry count'));
      } else {
        reject(new Error('Record not found'));
      }
    };
    getRequest.onerror = () => reject(new Error('Error getting record'));
  });
};

export const mergeAudioBlobs = async (interviewId) => {
  const chunks = await getAudioChunks(interviewId);

  if (chunks.length === 0) {
    return null;
  }

  const blobs = chunks.map(chunk => chunk.blob);
  const mergedBlob = new Blob(blobs, { type: blobs[0].type });

  return {
    blob: mergedBlob,
    totalDuration: chunks.reduce((acc, chunk) => acc + chunk.duration, 0),
    chunkCount: chunks.length,
  };
};

// ============================================
// PENDING SUBMISSIONS (Offline Queue)
// ============================================

/**
 * Add a submission to the offline queue
 * @param {Object} data - The submission data
 * @param {string} data.type - Type of submission ('interview', 'attachment', etc.)
 * @param {string} data.interviewId - Interview ID
 * @param {string} data.alias - Alias
 * @param {Object} data.payload - The data to send when online
 * @param {Array<{blob: Blob, duration: number}>} [data.pendingAudios] - Audios still to upload
 * @param {Array<Object>} [data.preuploadedAudioAttachments] - Audios already uploaded (S3) waiting only to be linked
 * @param {Blob} [data.audioBlob] - DEPRECATED single-blob path; kept for entries queued before the multi-audio refactor
 */
export const addPendingSubmission = async (data) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);

    const record = {
      id: generateId(),
      type: data.type || 'interview',
      interviewId: data.interviewId,
      alias: data.alias,
      payload: data.payload,
      pendingAudios: data.pendingAudios || [],
      preuploadedAudioAttachments: data.preuploadedAudioAttachments || [],
      audioBlob: data.audioBlob || null,
      createdAt: new Date().toISOString(),
      status: 'pending',
      retryCount: 0,
      lastError: null,
    };

    const request = store.add(record);

    request.onsuccess = () => resolve(record);
    request.onerror = () => reject(new Error('Error adding pending submission'));
  });
};

/**
 * Get all pending submissions
 */
export const getPendingSubmissions = async () => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readonly');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      const submissions = request.result.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      resolve(submissions);
    };
    request.onerror = () => reject(new Error('Error getting pending submissions'));
  });
};

/**
 * Get all submissions (pending and failed) for display
 */
export const getAllSubmissions = async () => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readonly');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const request = store.getAll();

    request.onsuccess = () => {
      const submissions = request.result.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      resolve(submissions);
    };
    request.onerror = () => reject(new Error('Error getting all submissions'));
  });
};

/**
 * Get pending submissions count
 */
export const getPendingSubmissionsCount = async () => {
  const submissions = await getPendingSubmissions();
  return submissions.length;
};

/**
 * Update a pending submission (e.g., after retry)
 */
export const updatePendingSubmission = async (id, updates) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        const updatedRecord = { ...record, ...updates };
        const updateRequest = store.put(updatedRecord);
        updateRequest.onsuccess = () => resolve(updatedRecord);
        updateRequest.onerror = () => reject(new Error('Error updating submission'));
      } else {
        reject(new Error('Submission not found'));
      }
    };
    getRequest.onerror = () => reject(new Error('Error getting submission'));
  });
};

/**
 * Mark a submission as synced and remove it
 */
export const markSubmissionSynced = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(new Error('Error removing synced submission'));
  });
};

/**
 * Mark a submission as failed
 */
export const markSubmissionFailed = async (id, error) => {
  return updatePendingSubmission(id, {
    status: 'failed',
    lastError: error?.message || 'Unknown error',
    retryCount: 0,
  });
};

/**
 * Increment retry count for a submission
 */
export const incrementSubmissionRetry = async (id, maxRetries = 5) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.retryCount += 1;
        if (record.retryCount >= maxRetries) {
          record.status = 'failed';
        }
        const updateRequest = store.put(record);
        updateRequest.onsuccess = () => resolve(record);
        updateRequest.onerror = () => reject(new Error('Error updating retry count'));
      } else {
        reject(new Error('Submission not found'));
      }
    };
    getRequest.onerror = () => reject(new Error('Error getting submission'));
  });
};

/**
 * Delete a pending submission
 */
export const deletePendingSubmission = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const request = store.delete(id);

    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(new Error('Error deleting submission'));
  });
};

/**
 * Clear all pending submissions for an interview
 */
export const clearInterviewSubmissions = async (interviewId) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PENDING_SUBMISSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_SUBMISSIONS);
    const index = store.index('interviewId');
    const request = index.getAll(interviewId);

    request.onsuccess = async () => {
      const submissions = request.result;
      for (const submission of submissions) {
        await deletePendingSubmission(submission.id);
      }
      resolve(true);
    };
    request.onerror = () => reject(new Error('Error clearing interview submissions'));
  });
};
